import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import {
  CloseRounded,
  PrecisionManufacturing as MachineIcon,
  AttachFileOutlined as FileIcon,
  DownloadOutlined as DownloadIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { CompanyServiceReport } from "../../api/serviceReportsApi";
import serviceReportsApi from "../../api/serviceReportsApi";
import { ServiceReportFile } from "../../../machinery/models/ServiceReportsModel";
import { ImagePreviewStrip, useGalleryImages } from "../../../../shared/components/gallery";
import type { GalleryImage } from "../../../../shared/components/gallery";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

// ─── Constants ─────────────────────────────────────────────
const PANEL_WIDTH = 480;
const TERRACOTTA = "#D4572B";
const TEXT_PRIMARY = "#0C1117";
const TEXT_BODY = "#4A4F57";
const MUTED = "#8C9099";
const WARM_CREAM = "#F7F5F0";
const BORDER = "#E8E5DF";
const FONT_SORA = "'Sora', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

// ─── Status badge config ───────────────────────────────────
const SR_STATUS: Record<number, { bg: string; text: string; labelKey: string }> = {
  1: { bg: "#EBF5FB", text: "#2980B9", labelKey: "statuses.inProgress" },
  2: { bg: "#EAFAF1", text: "#27AE60", labelKey: "myMachinery:serviceReportStatus.solved" },
};

// ─── Urgency badge config ──────────────────────────────────
const URGENCY_MAP: Record<number, { bg: string; labelKey: string }> = {
  1: { bg: "#DC2626", labelKey: "priorities.critical" },
  2: { bg: "#E8A817", labelKey: "priorities.high" },
  4: { bg: "#3B82F6", labelKey: "priorities.normal" },
  0: { bg: MUTED, labelKey: "serviceReports.urgency.info" },
};

// ─── Source type label ─────────────────────────────────────
const SOURCE_LABEL_KEYS: Record<number, string> = {
  1: "serviceReports.source.manual",
  2: "serviceReports.source.rfs",
  3: "serviceReports.source.maintenancePlan",
};

// ─── Note parsing (reused from InlineNoteSection pattern) ──
interface NoteEntry {
  author: string;
  date: string;
  text: string;
}

function parseNotes(notes: string | undefined, fallbackAuthor: string): NoteEntry[] {
  if (!notes || !notes.trim()) return [];
  const entryPattern = /^(.+?)\s*[—–-]\s*(\d{1,2}\s+\w{3}\s+\d{4},?\s*\d{2}:\d{2})\s*$/m;
  const lines = notes.split("\n");
  const entries: NoteEntry[] = [];
  let currentAuthor = fallbackAuthor;
  let currentDate = "";
  let currentTextLines: string[] = [];

  for (const line of lines) {
    const match = line.match(entryPattern);
    if (match) {
      if (currentTextLines.length > 0 || currentDate) {
        entries.push({ author: currentAuthor, date: currentDate, text: currentTextLines.join("\n").trim() });
      }
      currentAuthor = match[1].trim();
      currentDate = match[2].trim();
      currentTextLines = [];
    } else {
      if (line.trim() || currentTextLines.length > 0) {
        currentTextLines.push(line);
      }
    }
  }
  if (currentTextLines.length > 0 || currentDate) {
    entries.push({ author: currentAuthor, date: currentDate, text: currentTextLines.join("\n").trim() });
  }
  if (entries.length === 0) {
    return [{ author: fallbackAuthor, date: "", text: notes.trim() }];
  }
  return entries.filter((e) => e.text.trim());
}

// ─── File helpers ──────────────────────────────────────────
const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isImageFile = (name: string) => IMAGE_EXT.test(name);

function getFileIcon(name: string): string {
  const ext = name.toLowerCase().split(".").pop() || "";
  if (ext === "pdf") return "PDF";
  if (ext === "csv") return "CSV";
  if (["doc", "docx"].includes(ext)) return "DOC";
  if (["xls", "xlsx"].includes(ext)) return "XLS";
  return ext.toUpperCase().slice(0, 3) || "FILE";
}

// ─── Props ─────────────────────────────────────────────────
interface SrQuickViewPanelProps {
  open: boolean;
  serviceReportId: string;
  serviceReportNumber?: string;
  /** The machine ID from the linked SR, used to fetch full SR detail. */
  machineId?: string;
  workOrderId: string;
  onClose: () => void;
  onUnlinked: (srNumber: string) => void;
  translate: (key: string, options?: Record<string, unknown>) => string;
}

// ─── Component ─────────────────────────────────────────────
const SrQuickViewPanel: React.FC<SrQuickViewPanelProps> = ({
  open,
  serviceReportId,
  serviceReportNumber,
  machineId,
  workOrderId,
  onClose,
  onUnlinked,
  translate,
}) => {
  const navigate = useNavigate();
  const [sr, setSr] = useState<CompanyServiceReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [files, setFiles] = useState<ServiceReportFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);

  // ─── Fetch SR data when panel opens ────────────────────
  useEffect(() => {
    if (!open) return;
    setSr(null);
    setFiles([]);
    setLoading(true);

    // Try to find SR data from the company-wide endpoint
    serviceReportsApi
      .getCompanyServiceReports()
      .then((all) => {
        const found = all.find(
          (s) => s.id === serviceReportId || s.number === serviceReportNumber
        );
        if (found) {
          setSr(found);
          // If we have machineId and files aren't loaded, fetch detail for files
          const mid = found.machineId || machineId;
          const fCount = found.fileCount ?? found.files?.length ?? 0;
          if (mid && found.number && fCount > 0) {
            setFilesLoading(true);
            serviceReportsApi
              .getServiceReportDetail(mid, found.number)
              .then((detail) => {
                if (detail?.files) setFiles(detail.files);
              })
              .catch(() => {})
              .finally(() => setFilesLoading(false));
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, serviceReportId, serviceReportNumber, machineId]);

  // ─── Inject keyframes ──────────────────────────────────
  useEffect(() => {
    const id = "sr-qv-panel-keyframes";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        @keyframes srQvSlideIn {
          0% { transform: translateX(100%); }
          100% { transform: translateX(0); }
        }
        @keyframes srQvBackdropIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ─── Esc key handler ───────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  // ─── Lock body scroll ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // ─── Unlink handler ────────────────────────────────────
  const handleUnlink = useCallback(async () => {
    if (unlinking || !workOrderId || !serviceReportId) return;
    setUnlinking(true);
    try {
      await serviceReportsApi.unlinkSrFromWorkOrder(workOrderId, serviceReportId);
      onUnlinked(sr?.number || serviceReportNumber || serviceReportId);
    } catch {
      // Error handled by caller
    } finally {
      setUnlinking(false);
    }
  }, [unlinking, workOrderId, serviceReportId, sr, serviceReportNumber, onUnlinked]);

  // ─── Image gallery data ────────────────────────────────
  const imageFiles = useMemo<GalleryImage[]>(() => {
    if (!files || files.length === 0) return [];
    return files
      .filter((f) => {
        if (!f.content) return false;
        if (f.content.startsWith("data:image/")) return true;
        if (f.name && isImageFile(f.name)) return true;
        return false;
      })
      .map((f, idx) => ({
        id: `sr-qv-${f.name}-${idx}`,
        src: f.content,
        alt: f.name || `Image ${idx + 1}`,
        name: f.name,
      }));
  }, [files]);

  const nonImageFiles = useMemo(() => {
    if (!files || files.length === 0) return [];
    return files.filter((f) => {
      if (!f.content) return false;
      if (f.content.startsWith("data:image/")) return false;
      if (f.name && isImageFile(f.name)) return false;
      return true;
    });
  }, [files]);

  // ─── Derived data ──────────────────────────────────────
  const statusConfig = sr ? (SR_STATUS[sr.statusCode] || SR_STATUS[1]) : SR_STATUS[1];
  const urgencyConfig = sr ? (URGENCY_MAP[sr.urgency ?? 4] || URGENCY_MAP[4]) : URGENCY_MAP[4];
  const noteEntries = useMemo(
    () => parseNotes(sr?.notes, sr?.createdByName || translate("serviceReports.unknownReporter")),
    [sr?.notes, sr?.createdByName, translate]
  );

  const srDisplayNumber = sr?.number
    ? (sr.number.startsWith("SR-") ? sr.number : `SR-${sr.number.length > 4 ? sr.number.slice(-4) : sr.number}`)
    : serviceReportNumber
      ? (serviceReportNumber.startsWith("SR-") ? serviceReportNumber : `SR-${serviceReportNumber}`)
      : "SR-???";

  const machineLabel = [
    sr?.machineModel || (sr as any)?.manufacturerName,
    sr?.machineSerialNumber ? `SN: ${sr.machineSerialNumber}` : (sr?.machineInternalNumber ? `SN: ${sr.machineInternalNumber}` : null),
    sr?.locationName,
  ].filter(Boolean).join(" \u00B7 ");

  const totalFileCount = (sr?.fileCount ?? files.length) || 0;
  const sourceLabel = sr?.sourceType
    ? (SOURCE_LABEL_KEYS[sr.sourceType] ? translate(SOURCE_LABEL_KEYS[sr.sourceType]) : translate("common:unknown"))
    : translate("serviceReports.source.manual");

  if (!open) return null;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ position: "fixed", inset: 0, zIndex: 1400, display: "flex" }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(12,17,23,0.18)",
          animation: "srQvBackdropIn 0.2s ease",
        }}
      />

      {/* Panel */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: `${PANEL_WIDTH}px`,
          maxWidth: "100vw",
          backgroundColor: "#FFFFFF",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          animation: "srQvSlideIn 0.3s ease",
        }}
      >
        {/* ─── Terracotta accent line ─────────────────── */}
        <Box
          sx={{
            height: "4px",
            background: `linear-gradient(90deg, ${TERRACOTTA}, #E8A817)`,
            flexShrink: 0,
          }}
        />

        {/* ─── Header ─────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: `1px solid ${BORDER}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ fontFamily: FONT_SORA, fontSize: "16px", fontWeight: 700, color: TEXT_PRIMARY }}>
              {translate("serviceReports.quickView.title")}
            </Box>
            <Box sx={{ fontFamily: FONT_SORA, fontSize: "12px", color: MUTED, marginTop: "2px" }}>
              {translate("serviceReports.quickView.subtitle")}
            </Box>
          </Box>
          <Box
            onClick={onClose}
            sx={{
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F2F0EB",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.15s ease",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#E8E5DF" },
            }}
          >
            <CloseRounded sx={{ fontSize: 18, color: TEXT_BODY }} />
          </Box>
        </Box>

        {/* ─── Scrollable body ────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            padding: "20px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 0,
            scrollbarWidth: "thin",
            scrollbarColor: "#C1C7CD transparent",
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#C1C7CD", borderRadius: "3px" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
              <Box
                sx={{
                  width: "24px",
                  height: "24px",
                  border: "3px solid #E8E5DF",
                  borderTopColor: TERRACOTTA,
                  borderRadius: "50%",
                  animation: "spin 0.6s linear infinite",
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
            </Box>
          ) : !sr ? (
            <Box sx={{ fontFamily: FONT_SORA, fontSize: "13px", color: MUTED, textAlign: "center", padding: "40px 0" }}>
              {translate("serviceReports.quickView.notFound")}
            </Box>
          ) : (
            <>
              {/* SR number + status + urgency badges */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
                <Box sx={{ fontFamily: FONT_MONO, fontSize: "14px", fontWeight: 600, color: TERRACOTTA }}>
                  {srDisplayNumber}
                </Box>
                <Box
                  sx={{
                    fontFamily: FONT_SORA,
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    backgroundColor: statusConfig.bg,
                    color: statusConfig.text,
                  }}
                >
                  {translate(statusConfig.labelKey)}
                </Box>
                <Box
                  sx={{
                    fontFamily: FONT_SORA,
                    fontSize: "10px",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    padding: "2px 10px",
                    borderRadius: "4px",
                    backgroundColor: urgencyConfig.bg,
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Box
                    sx={{
                      width: "5px",
                      height: "5px",
                      borderRadius: "50%",
                      backgroundColor: "#FFFFFF",
                      flexShrink: 0,
                    }}
                  />
                  {translate(urgencyConfig.labelKey)}
                </Box>
              </Box>

              {/* Title */}
              <Box
                sx={{
                  fontFamily: FONT_SORA,
                  fontSize: "16px",
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  lineHeight: 1.35,
                  marginBottom: "14px",
                }}
              >
                {sr.title}
              </Box>

              {/* Machine context chip */}
              {machineLabel && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    backgroundColor: WARM_CREAM,
                    borderRadius: "8px",
                    padding: "10px 14px",
                    marginBottom: "20px",
                  }}
                >
                  <MachineIcon sx={{ fontSize: 18, color: TERRACOTTA }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontFamily: FONT_SORA,
                        fontSize: "13px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sr.machineModel || (sr as any)?.manufacturerName || translate("serviceReports.unknownMachine")}
                    </Box>
                    <Box
                      sx={{
                        fontFamily: FONT_MONO,
                        fontSize: "11px",
                        color: MUTED,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {[
                        sr.machineSerialNumber ? `SN: ${sr.machineSerialNumber}` : (sr.machineInternalNumber ? `SN: ${sr.machineInternalNumber}` : null),
                        sr.locationName,
                      ].filter(Boolean).join(" \u00B7 ")}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* DESCRIPTION */}
              {sr.description && (
                <Box sx={{ marginBottom: "20px" }}>
                  <SectionLabel>{translate("serviceReports.expanded.description")}</SectionLabel>
                  <Box
                    sx={{
                      fontFamily: FONT_SORA,
                      fontSize: "13px",
                      color: TEXT_BODY,
                      lineHeight: 1.65,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {sr.description}
                  </Box>
                </Box>
              )}

              {/* TECHNICAL NOTES */}
              {noteEntries.length > 0 && (
                <Box sx={{ marginBottom: "20px" }}>
                  <SectionLabel>{translate("serviceReports.expanded.technicalNotes")}</SectionLabel>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {noteEntries.map((entry, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          borderLeft: "3px solid #3B82F6",
                          padding: "10px 14px",
                          backgroundColor: "#F4F6F9",
                          borderRadius: "0 8px 8px 0",
                        }}
                      >
                        {entry.date && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px", flexWrap: "wrap", gap: "4px" }}>
                            <Box sx={{ fontFamily: FONT_SORA, fontSize: "12px", fontWeight: 600, color: TEXT_PRIMARY }}>
                              {entry.author}
                            </Box>
                            <Box sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: MUTED }}>
                              {entry.date}
                            </Box>
                          </Box>
                        )}
                        <Box
                          sx={{
                            fontFamily: FONT_MONO,
                            fontSize: "12px",
                            color: TEXT_BODY,
                            lineHeight: 1.6,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {entry.text}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Divider */}
              <Box sx={{ height: "1px", backgroundColor: BORDER, marginBottom: "20px" }} />

              {/* ATTACHMENTS */}
              <Box sx={{ marginBottom: "20px" }}>
                <SectionLabel icon={<FileIcon sx={{ fontSize: 13, color: MUTED }} />}>
                  {translate("serviceReports.expanded.attachments")}
                </SectionLabel>

                {filesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontFamily: FONT_SORA, fontSize: "12px", color: MUTED, py: "8px" }}>
                    <Box
                      sx={{
                        width: "14px",
                        height: "14px",
                        border: "2px solid #E8E5DF",
                        borderTopColor: TERRACOTTA,
                        borderRadius: "50%",
                        animation: "spin 0.6s linear infinite",
                        "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                      }}
                    />
                    {translate("serviceReports.expanded.loadingAttachments")}
                  </Box>
                ) : imageFiles.length === 0 && nonImageFiles.length === 0 ? (
                  <Box sx={{ fontFamily: FONT_SORA, fontSize: "12px", color: MUTED, padding: "4px 0" }}>
                    {translate("serviceReports.quickView.noAttachments")}
                  </Box>
                ) : (
                  <>
                    {/* Image thumbnails via shared component */}
                    {imageFiles.length > 0 && (
                      <Box sx={{ marginBottom: nonImageFiles.length > 0 ? "10px" : "0" }}>
                        <ImagePreviewStrip
                          images={imageFiles}
                          maxVisible={4}
                          thumbnailSize={64}
                          gap={8}
                          borderRadius="8px"
                        />
                      </Box>
                    )}

                    {/* Non-image files as chips */}
                    {nonImageFiles.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: imageFiles.length > 0 ? "8px" : "0" }}>
                        {nonImageFiles.map((f, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              backgroundColor: WARM_CREAM,
                              border: `1px solid ${BORDER}`,
                              borderRadius: "6px",
                              padding: "5px 10px",
                              fontFamily: FONT_MONO,
                              fontSize: "11px",
                              color: TEXT_BODY,
                              cursor: "default",
                            }}
                          >
                            <Box
                              sx={{
                                fontFamily: FONT_SORA,
                                fontSize: "8px",
                                fontWeight: 700,
                                color: "#FFFFFF",
                                backgroundColor: MUTED,
                                borderRadius: "3px",
                                padding: "1px 4px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {getFileIcon(f.name)}
                            </Box>
                            <Box
                              sx={{
                                maxWidth: "140px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {f.name}
                            </Box>
                            {f.content && (
                              <Box
                                component="a"
                                href={f.content}
                                download={f.name}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  color: MUTED,
                                  "&:hover": { color: TERRACOTTA },
                                }}
                              >
                                <DownloadIcon sx={{ fontSize: 13 }} />
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* File count */}
                    <Box sx={{ fontFamily: FONT_MONO, fontSize: "11px", color: MUTED, marginTop: "8px" }}>
                      {translate("serviceReports.fileCount", { count: totalFileCount })}
                      {" \u00B7 "}
                      {translate("serviceReports.quickView.clickToOpenLightbox")}
                    </Box>
                  </>
                )}
              </Box>

              {/* DETAILS — 2-column metadata grid */}
              <Box sx={{ marginBottom: "8px" }}>
                <SectionLabel>{translate("serviceReports.quickView.details")}</SectionLabel>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0",
                  }}
                >
                  <DetailCell
                    label={translate("serviceReports.expanded.alarmCode")}
                    value={sr.alarmCode || "—"}
                    mono
                  />
                  <DetailCell
                    label={translate("serviceReports.quickView.reportedBy")}
                    value={sr.createdByName || translate("serviceReports.unknownReporter")}
                  />
                  <DetailCell
                    label={translate("serviceReports.expanded.opened")}
                    value={sr.openDate ? formatUiDate(sr.openDate) : "—"}
                    mono
                    highlight
                  />
                  <DetailCell
                    label={translate("serviceReports.quickView.closed")}
                    value={
                      sr.closeDate
                        ? formatUiDate(sr.closeDate)
                        : translate("serviceReports.quickView.notYetResolved")
                    }
                  />
                  <DetailCell
                    label={translate("serviceReports.quickView.source")}
                    value={sourceLabel}
                  />
                  <DetailCell
                    label={translate("serviceReports.quickView.urgency")}
                    value={translate(urgencyConfig.labelKey)}
                    valueColor={urgencyConfig.bg}
                  />
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* ─── Sticky Footer ──────────────────────────── */}
        {sr && (
          <Box
            sx={{
              borderTop: `1px solid ${BORDER}`,
              padding: "16px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            {/* View Full Details button */}
            <Box
              onClick={() => {
                if (sr.machineId && sr.number) {
                  navigate(`/machinery/${sr.machineId}/service-reports/${sr.number}`);
                }
              }}
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "14px",
                fontWeight: 600,
                color: "#FFFFFF",
                backgroundColor: TERRACOTTA,
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: sr.machineId ? "pointer" : "default",
                textAlign: "center",
                transition: "all 0.15s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                "&:hover": sr.machineId
                  ? {
                      backgroundColor: "#BF4D26",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(212,87,43,0.25)",
                    }
                  : {},
              }}
            >
              {translate("serviceReports.quickView.viewFullDetails")}
              <Box component="span" sx={{ fontSize: "14px" }}>{"\u2197"}</Box>
            </Box>

            {/* Unlink button */}
            <Box
              onClick={handleUnlink}
              sx={{
                fontFamily: FONT_SORA,
                fontSize: "14px",
                fontWeight: 600,
                color: unlinking ? MUTED : TERRACOTTA,
                backgroundColor: "transparent",
                border: `1.5px solid ${unlinking ? BORDER : TERRACOTTA}`,
                padding: "11px 24px",
                borderRadius: "8px",
                cursor: unlinking ? "default" : "pointer",
                textAlign: "center",
                transition: "all 0.15s ease",
                "&:hover": unlinking
                  ? {}
                  : {
                      backgroundColor: "rgba(212,87,43,0.04)",
                    },
              }}
            >
              {unlinking
                ? translate("serviceReports.quickView.unlinking")
                : translate("serviceReports.quickView.unlinkFromWorkOrder")}
            </Box>

            {/* Footer hint */}
            <Box sx={{ fontFamily: FONT_SORA, fontSize: "11px", color: MUTED, textAlign: "center" }}>
              {translate("serviceReports.quickView.footerHint")}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Sub-components ──────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode; icon?: React.ReactNode }> = ({ children, icon }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      fontFamily: FONT_SORA,
      fontSize: "11px",
      fontWeight: 600,
      color: MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.1em",
      marginBottom: "10px",
    }}
  >
    {icon}
    {children}
  </Box>
);

interface DetailCellProps {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
  valueColor?: string;
}

const DetailCell: React.FC<DetailCellProps> = ({ label, value, mono, highlight, valueColor }) => (
  <Box
    sx={{
      padding: "10px 0",
      borderBottom: `1px solid #F2F0EB`,
    }}
  >
    <Box
      sx={{
        fontFamily: FONT_SORA,
        fontSize: "11px",
        color: MUTED,
        marginBottom: "3px",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: mono ? FONT_MONO : FONT_SORA,
        fontSize: "13px",
        fontWeight: 600,
        color: valueColor || (highlight ? TERRACOTTA : TEXT_PRIMARY),
        letterSpacing: mono ? "0.02em" : undefined,
      }}
    >
      {value}
    </Box>
  </Box>
);

export default SrQuickViewPanel;
